import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiGet, apiDelete } from 'api'
import { baseUrl } from 'api/helpers'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import axios from 'axios'

const getPolicyDocuments = async (token, municipalityId) => {
  const url = `municipalities/${municipalityId}/policy_documents`
  const data = await apiGet({ url, token })
  return data
}

export const usePolicyDocuments = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)

  const queryFn = () => getPolicyDocuments(token, municipality_id)
  const queryKey = ['policy-documents', municipality_id]

  return useQuery(queryKey, queryFn, { ...options })
}

const getPolicyDocument = async (token, municipalityId, policyDocumentId) => {
  const url = `municipalities/${municipalityId}/policy_documents/${policyDocumentId}`
  const data = await apiGet({ url, token })
  return data
}

export const usePolicyDocument = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)

  const queryFn = () => getPolicyDocument(token, municipality_id, policyDocumentId)
  const queryKey = ['policy-document', policyDocumentId]

  return useQuery(queryKey, queryFn, {
    enabled: !!policyDocumentId,
    ...options
  })
}

// document_url is a Rails-relative storage path, so fetch it against the API
// origin with the bearer token and save it as a blob. Navigating to it directly
// would resolve against the frontend origin and trigger an Auth0 redirect.
const downloadPolicyDocumentFile = async (token, documentUrl, fileName) => {
  const fullUrl = new URL(documentUrl, baseUrl).toString()
  const response = await axios.get(fullUrl, {
    responseType: 'blob',
    headers: { Authorization: `Bearer ${token}` }
  })

  const blobUrl = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName || 'policy-document.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}

export const useDownloadPolicyDocument = ({ options = {} } = {}) => {
  const token = useContext(AuthContext)

  return useMutation(
    ({ documentUrl, fileName }) => downloadPolicyDocumentFile(token, documentUrl, fileName),
    { ...options }
  )
}

const uploadPolicyDocument = async (token, municipalityId, name, file) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents`
  const formData = new FormData()
  formData.append('policy_document[document]', file)
  formData.append('policy_document[name]', name)

  const response = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`
    }
  })

  return response.data
}

export const useUploadPolicyDocument = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const { onSuccess: externalOnSuccess, ...remainingOptions } = options

  return useMutation(
    ({ name, file }) => uploadPolicyDocument(token, municipality_id, name, file),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        externalOnSuccess?.(...args)
      },
      ...remainingOptions
    }
  )
}

const createPolicyDocument = async (token, municipalityId, name, sections) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents`
  const policy_document_sections_attributes = (sections || []).map((id) => ({ section_id: id }))
  const response = await axios.post(url, { policy_document: { name, policy_document_sections_attributes } }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useCreatePolicyDocument = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const { onSuccess: externalOnSuccess, ...remainingOptions } = options

  return useMutation(
    ({ name, sections }) => createPolicyDocument(token, municipality_id, name, sections),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        externalOnSuccess?.(...args)
      },
      ...remainingOptions
    }
  )
}

const uploadToExistingPolicyDocument = async (token, municipalityId, policyDocumentId, file) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}`
  const formData = new FormData()
  formData.append('policy_document[document]', file)

  const response = await axios.patch(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useUploadToExistingPolicyDocument = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const { onSuccess: externalOnSuccess, ...remainingOptions } = options

  return useMutation(
    ({ policyDocumentId, file }) => uploadToExistingPolicyDocument(token, municipality_id, policyDocumentId, file),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        externalOnSuccess?.(...args)
      },
      ...remainingOptions
    }
  )
}

const deletePolicyDocument = async (token, municipalityId, policyDocumentId) => {
  const url = `municipalities/${municipalityId}/policy_documents/${policyDocumentId}`
  const response = await apiDelete({ url, token })
  return response
}

export const useDeletePolicyDocument = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (policyDocumentId) => deletePolicyDocument(token, municipality_id, policyDocumentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const updatePolicyDocumentSections = async (token, municipalityId, policyDocumentId, sectionIds) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/update_sections`
  const response = await axios.patch(url, { section_ids: sectionIds }, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
  })
  return response.data
}

export const useUpdatePolicyDocumentSections = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (sectionIds) => updatePolicyDocumentSections(token, municipality_id, policyDocumentId, sectionIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}

const updateRenderedHtml = async (token, municipalityId, policyDocumentId, html) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rendered_html`
  const response = await axios.patch(url, { rendered_html: html }, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
  })
  return response.data
}

export const useUpdateRenderedHtml = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (html) => updateRenderedHtml(token, municipality_id, policyDocumentId, html),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}

const matchAnalytics = async (token, municipalityId, policyDocumentId) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/match_analytics`
  const response = await axios.post(url, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useMatchAnalytics = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (policyDocumentId) => matchAnalytics(token, municipality_id, policyDocumentId),
    {
      onSuccess: (_data, policyDocumentId) => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}

const extractPolicyRules = async (token, municipalityId, policyDocumentId) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/extract_rules`
  const response = await axios.post(url, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useExtractPolicyRules = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (policyDocumentId) => extractPolicyRules(token, municipality_id, policyDocumentId),
    {
      onSuccess: (_data, policyDocumentId) => {
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}
